import { fmtDataIT, isoData } from "../lib/format";

import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, RefreshCw, Receipt, History } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtEUR, fmtNum } from "../lib/format";
import { EVENTI, MOMENTI_ANNULLO, RESO_VALORI, STATI_CONFERMA, STATI_CONSEGNA, STATI_ORDINE, FAMIGLIE, DETTAGLI, STATI_INCASSO_COD, INCASSO_LABELS, STATO_BADGE, CONFERMA_BADGE, ORDINE_BADGE, CONSEGNA_BADGE, EVENTO_BADGE, MOMENTO_BADGE, RESO_BADGE, FAMIGLIA_BADGE } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { InfoTip } from "../components/InfoTip";

const COLONNE_ORDINI = [
  { key: "data", h: "Data" },
  { key: "n-ordine", h: "N° ordine" },
  { key: "store", h: "Store", masterOnly: true },
  { key: "cliente", h: "Cliente" },
  { key: "pagamento", h: "Pagamento" },
  { key: "totale-lordo", h: "Totale lordo", tip: "Valore dell'ordine al momento dell'acquisto, prima di eventuali storni" },
  { key: "totale-incassato", h: "Totale incassato", tip: "Quanto è stato effettivamente incassato per questo ordine" },
  { key: "conferma", h: "Conferma" },
  { key: "stato", h: "Stato" },
  { key: "consegna", h: "Consegna" },
  { key: "momento-annullo", h: "Momento annullo" },
  { key: "reso", h: "Reso" },
  { key: "evento", h: "Evento" },
  { key: "data-spedizione", h: "Data spedizione" },
  { key: "data-consegna", h: "Data consegna" },
  { key: "stato-incasso-cod", h: "Stato incasso COD" },
  { key: "data-incasso-cod", h: "Data incasso COD" },
  { key: "importo-incassato-cod", h: "Importo incassato COD" },
];

const COLONNE_CALC = [
  { key: "imponibile", h: "Imponibile", tip: "Totale dell'ordine al netto dell'IVA: totale ordine ÷ (1 + aliquota). Calcolato sull'intero valore dell'ordine, anche se l'incasso non è ancora avvenuto" },
  { key: "iva-incassata", h: "IVA sulla vendita", tip: "IVA contenuta nel totale dell'ordine: totale ordine − imponibile" },
  { key: "costo-merce", h: "Costo merce", tip: "Somma di quantità × costo unitario delle righe ordine. Per gli store figli è il prezzo di cessione" },
  { key: "iva-merce", h: "IVA merce", tip: "IVA sul costo della merce: è recuperabile, quindi non viene sottratta dal margine" },
  { key: "costo-spedizione", h: "Costo spedizione", tip: "Costo della spedizione: reale se già disponibile, altrimenti stimato dai parametri dello store (segno ≈)" },
  { key: "commissione-gateway", h: "Commissione gateway", tip: "Commissione del metodo di pagamento: totale ordine × percentuale gateway congelata sull'ordine. Sul contrassegno è zero" },
  { key: "compensazione-iva", h: "Compensazione IVA", tip: "IVA sulla vendita meno IVA su merce e spedizioni: quanto resta da versare se l'ordine va a buon fine" },
  { key: "margine", h: "Margine", tip: "Imponibile meno merce, spedizione e commissioni, calcolato come se l'ordine andasse a buon fine. Per gli ordini annullati: solo i costi previsti per l'annullo" },
];

const COLONNE_CALC_MASTER = [
  { key: "costo-merce-reale", h: "Costo merce reale", tip: "Costo della merce al fornitore: stesso SKU cercato sul catalogo dello store master. Se sul master il costo coincide col prezzo (tool di importazione), è ricostruito sottraendo il ricarico fisso della collezione" },
  { key: "ricarico-merce", h: "Ricarico merce", tip: "Costo merce a cessione meno costo reale: quanto resta al gruppo sulla merce per questo ordine. Il segno ≈ indica che deriva da costi ricostruiti (stimato, non misurato)" },
];

function EditCell({ value, display, onSave, options, type = "text", testid, disabled, stickyProps }) {
  const [ed, setEd] = useState(false);
  const [v, setV] = useState(value ?? "");
  const sp = stickyProps || {};
  const cls = (base) => `${base} ${sp.className || ""}`;
  if (disabled) {
    return <td data-testid={testid} style={sp.style} className={cls("px-2 py-1.5 text-sm whitespace-nowrap text-slate-700")}>{display ?? (value ?? "—")}</td>;
  }
  if (!ed) {
    return (
      <td data-testid={testid} style={sp.style} onClick={() => { setV(type === "date" ? isoData(value) : (value ?? "")); setEd(true); }}
        className={cls("px-2 py-1.5 text-sm cursor-pointer hover:bg-indigo-50/60 border border-transparent hover:border-indigo-300 rounded-xs whitespace-nowrap text-slate-700")}
        title="Clicca per modificare">
        {display ?? (value ?? "—")}
      </td>
    );
  }
  if (options) {
    return (
      <td style={sp.style} className={cls("p-0")}>
        <select autoFocus value={v ?? ""} data-testid={`${testid}-select`}
          onChange={(e) => { onSave(e.target.value === "" ? null : e.target.value); setEd(false); }}
          onBlur={() => setEd(false)}
          className="text-sm w-full px-2 py-1.5 border border-indigo-400 outline-none bg-white">
          <option value="">—</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </td>
    );
  }
  return (
    <td style={sp.style} className={cls("p-0")}>
      <input autoFocus type={type} step="0.01" value={v} data-testid={`${testid}-input`}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          setEd(false);
          const prima = type === "date" ? isoData(value) : (value ?? "");
          if (String(v) !== String(prima)) onSave(type === "number" ? (parseFloat(v) || 0) : type === "date" ? (v || null) : v);
        }}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEd(false); }}
        className={`text-sm w-full px-2 py-1.5 border border-indigo-400 outline-none ${type === "date" ? "min-w-[138px] bg-white" : ""}`} />
    </td>
  );
}

const CalcCell = ({ children, testid }) => (
  <td data-testid={testid} title="Campo calcolato — non modificabile"
    className="px-2 py-1.5 text-sm font-mono text-right bg-slate-100/80 border-l-2 border-l-slate-300 text-slate-800 cursor-not-allowed select-none whitespace-nowrap tnum">
    <Lock className="w-3 h-3 inline mr-1 text-slate-400" />{children}
  </td>
);

function RigheDialog({ ordine, onClose, onSaved }) {
  const [righe, setRighe] = useState(ordine?.righe?.map((r) => ({ ...r })) || []);
  useEffect(() => { setRighe(ordine?.righe?.map((r) => ({ ...r })) || []); }, [ordine]);
  if (!ordine) return null;
  const set = (i, k, val) => setRighe((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: val } : r)));
  const save = async () => {
    try {
      const payload = righe.map((r) => ({
        sku: r.sku || null, nome: r.nome || null, quantita: parseFloat(r.quantita) || 1,
        prezzo: parseFloat(r.prezzo) || 0,
        costo_unitario: r.costo_unitario === "" || r.costo_unitario == null ? null : parseFloat(r.costo_unitario),
      }));
      const res = await api.put(`/ordini/${ordine.id}/righe`, payload);
      toast.success("Righe salvate");
      onSaved(res.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={!!ordine} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="righe-dialog">
        <DialogHeader><DialogTitle>Righe ordine {ordine.id_ordine_esterno}</DialogTitle></DialogHeader>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
            <th className="py-1">SKU</th><th>Quantità</th><th>Prezzo</th><th>Costo unit.</th><th /></tr></thead>
          <tbody>
            {righe.map((r, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="py-1 pr-2"><input data-testid={`riga-sku-${i}`} className="border rounded px-2 py-1 w-28 text-sm" value={r.sku || ""} onChange={(e) => set(i, "sku", e.target.value)} /></td>
                <td className="pr-2"><input data-testid={`riga-qta-${i}`} type="number" className="border rounded px-2 py-1 w-20 text-sm text-right" value={r.quantita} onChange={(e) => set(i, "quantita", e.target.value)} /></td>
                <td className="pr-2"><input data-testid={`riga-prezzo-${i}`} type="number" step="0.01" className="border rounded px-2 py-1 w-24 text-sm text-right" value={r.prezzo} onChange={(e) => set(i, "prezzo", e.target.value)} /></td>
                <td className="pr-2"><input data-testid={`riga-costo-${i}`} type="number" step="0.01" className="border rounded px-2 py-1 w-24 text-sm text-right" value={r.costo_unitario ?? ""} onChange={(e) => set(i, "costo_unitario", e.target.value)} /></td>
                <td><button className="text-red-500 text-xs" onClick={() => setRighe((rs) => rs.filter((_, j) => j !== i))}>Elimina</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between mt-3">
          <button data-testid="riga-add-button" onClick={() => setRighe((rs) => [...rs, { sku: "", quantita: 1, prezzo: 0, costo_unitario: null }])}
            className="text-sm text-indigo-600 hover:underline">+ Aggiungi riga</button>
          <button data-testid="righe-save-button" onClick={save}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-1.5">Salva righe</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogDialog({ ordineId, onClose }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => { if (ordineId) api.get(`/ordini/${ordineId}/log`).then((r) => setLogs(r.data)); }, [ordineId]);
  if (!ordineId) return null;
  return (
    <Dialog open={!!ordineId} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="log-dialog">
        <DialogHeader><DialogTitle>Log modifiche ordine</DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
              <th className="py-1">Quando</th><th>Utente</th><th>Campo</th><th>Prima</th><th>Dopo</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-50">
                  <td className="py-1 font-mono text-xs">{new Date(l.timestamp).toLocaleString("it-IT")}</td>
                  <td className="text-xs">{l.utente}</td>
                  <td className="font-medium">{l.campo}</td>
                  <td className="font-mono text-xs">{l.valore_prima ?? "—"}</td>
                  <td className="font-mono text-xs">{l.valore_dopo ?? "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-slate-400">Nessuna modifica registrata</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NuovoOrdineDialog({ open, onClose, stores, defaultStoreId, onCreated, isMaster }) {
  const [f, setF] = useState({ data: "", id_ordine_esterno: "", nome_cliente: "", totale_ordine_lordo: "", totale_incassato: "", metodo_raw: "", righe: "" });
  const [storeId, setStoreId] = useState(defaultStoreId);
  useEffect(() => { setStoreId(defaultStoreId); }, [defaultStoreId, open]);
  const save = async () => {
    try {
      const righe = (f.righe || "").split("|").filter(Boolean).map((p) => {
        const [sku, q, pr] = p.split(":");
        return { sku: sku || null, nome: null, quantita: parseFloat(q) || 1, prezzo: parseFloat((pr || "0").replace(",", ".")) || 0, costo_unitario: null };
      });
      const res = await api.post("/ordini", {
        store_id: storeId, data: f.data, id_ordine_esterno: f.id_ordine_esterno,
        nome_cliente: f.nome_cliente, totale_ordine_lordo: parseFloat(String(f.totale_ordine_lordo).replace(",", ".")) || 0,
        totale_incassato: parseFloat(String(f.totale_incassato).replace(",", ".")) || 0,
        metodo_raw: f.metodo_raw || null, righe,
      });
      toast.success("Ordine creato");
      onCreated(res.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const inp = "border border-slate-300 rounded-md px-2 py-1.5 text-sm w-full";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="nuovo-ordine-dialog">
        <DialogHeader><DialogTitle>Nuovo ordine manuale</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {isMaster && (
            <label className="text-xs text-slate-500 col-span-2">Store
              <select data-testid="nuovo-ordine-store" className={inp} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select></label>
          )}
          <label className="text-xs text-slate-500">Data<input data-testid="nuovo-ordine-data" type="date" className={inp} value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} /></label>
          <label className="text-xs text-slate-500">N° ordine<input data-testid="nuovo-ordine-id" className={inp} value={f.id_ordine_esterno} onChange={(e) => setF({ ...f, id_ordine_esterno: e.target.value })} /></label>
          <label className="text-xs text-slate-500 col-span-2">Cliente<input className={inp} value={f.nome_cliente} onChange={(e) => setF({ ...f, nome_cliente: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Totale lordo €<input data-testid="nuovo-ordine-lordo" className={inp} value={f.totale_ordine_lordo} onChange={(e) => setF({ ...f, totale_ordine_lordo: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Incassato €<input data-testid="nuovo-ordine-incassato" className={inp} value={f.totale_incassato} onChange={(e) => setF({ ...f, totale_incassato: e.target.value })} /></label>
          <label className="text-xs text-slate-500 col-span-2">Metodo pagamento (grezzo)<input className={inp} placeholder="es. Contrassegno, PayPal…" value={f.metodo_raw} onChange={(e) => setF({ ...f, metodo_raw: e.target.value })} /></label>
          <label className="text-xs text-slate-500 col-span-2">Righe (SKU:qta:prezzo, separate da |)<input data-testid="nuovo-ordine-righe" className={inp} placeholder="SKU1:2:19,90|SKU2:1:9,90" value={f.righe} onChange={(e) => setF({ ...f, righe: e.target.value })} /></label>
        </div>
        <button data-testid="nuovo-ordine-save" onClick={save}
          className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-2 w-full">Crea ordine</button>
      </DialogContent>
    </Dialog>
  );
}

const Badge = ({ v, map, testid }) => (
  <span data-testid={testid} className={`text-xs border rounded-full px-2 py-0.5 whitespace-nowrap ${(map || STATO_BADGE)[v] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{v || "—"}</span>
);

function DettaglioOrdine({ ordine, onClose }) {
  if (!ordine) return null;
  const o = ordine;
  const Row = ({ label, children }) => (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-50 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 text-right">{children}</span>
    </div>
  );
  return (
    <Dialog open={!!ordine} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dettaglio-ordine-dialog">
        <DialogHeader><DialogTitle className="font-mono">{o.id_ordine_esterno}</DialogTitle></DialogHeader>
        <Row label="Data">{fmtDataIT(o.data)}</Row>
        <Row label="Cliente">{o.nome_cliente || "—"}</Row>
        <Row label="Pagamento">{o.metodo_dettaglio || o.metodo_famiglia || o.metodo_raw || "—"}</Row>
        <Row label="Consegna"><Badge v={o.stato_consegna} /></Row>
        <Row label="Conferma"><Badge v={o.stato_conferma} /></Row>
        <Row label="Incasso COD">{INCASSO_LABELS[o.stato_incasso_effettivo] || "—"}</Row>
        <Row label="Totale lordo">{fmtEUR(o.totale_ordine_lordo)}</Row>
        {o.calc?.totale_reso > 0 && (
          <Row label="Resi / rimborsi">
            {`−${fmtEUR(o.calc.totale_reso)} di merce resa · restituiti ${fmtEUR(o.calc.totale_rimborsato)}${o.calc.reso_totale ? " · reso totale: ricavo azzerato, restano i costi degli eventi" : ""}`}
          </Row>
        )}
        <Row label="Incassato">{fmtEUR(o.totale_incassato)}</Row>
        <Row label="Imponibile">{fmtEUR(o.calc.vista.imponibile)}</Row>
        <Row label="Costo merce">{fmtEUR(o.calc.vista.costo_merce)}</Row>
        {o.calc.costo_merce_reale != null && <Row label="Costo merce reale (master)">{fmtEUR(o.calc.costo_merce_reale)}</Row>}
        {o.calc.ricarico_merce != null && <Row label="Ricarico merce (master)">{fmtEUR(o.calc.ricarico_merce)}</Row>}
        <Row label="Costo spedizione">
          {o.calc.spedizione_stimata ? "≈ " : ""}{fmtEUR(o.calc.vista.costo_spedizione)}
          <span className="text-[11px] text-slate-400 ml-1">{o.calc.spedizione_stimata ? "stimata" : "reale"}</span>
        </Row>
        <Row label="IVA sulla vendita">{fmtEUR(o.calc.vista.iva_vendita)}</Row>
        <Row label="Compensazione IVA">{fmtEUR(o.calc.vista.compensazione)}</Row>
        <Row label="Margine (se va a buon fine)">{fmtEUR(o.calc.vista.margine)}</Row>
      </DialogContent>
    </Dialog>
  );
}

export default function OrdiniPage() {
  const { stores, storeId, from, to, isMaster } = useOutletContext();
  const [ordini, setOrdini] = useState(null);
  const [meta, setMeta] = useState({ totale: 0, page: 1, per_page: 50, ricerca_globale: false });
  const [righeOrdine, setRigheOrdine] = useState(null);
  const [logOrdineId, setLogOrdineId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [filtri, setFiltri] = useState({ stato_consegna: "", stato_conferma: "", metodo: "", importo_min: "", importo_max: "" });
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const filtriKey = JSON.stringify(filtri);
  const EC = (props) => <EditCell {...props} disabled={!isMaster} />;

  // Colonne identificative adesive a sinistra: selezione, data, n° ordine, [store], cliente.
  const STICKY_W = [36, 108, 132, ...(isMaster ? [104] : []), 176];
  const STICKY_LEFT = STICKY_W.map((_, i) => STICKY_W.slice(0, i).reduce((a, b) => a + b, 0));
  const stickyStyle = (i) => ({ position: "sticky", left: STICKY_LEFT[i], width: STICKY_W[i], minWidth: STICKY_W[i], maxWidth: STICKY_W[i] });
  const sp = (i, last = false) => ({ style: stickyStyle(i), className: `sticky-l truncate${last ? " sticky-last" : ""}` });
  const STICKY_IDX = { data: 1, "n-ordine": 2, ...(isMaster ? { store: 3 } : {}), cliente: isMaster ? 4 : 3 };
  const LAST_STICKY = isMaster ? 4 : 3;
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Evidenziazione colonna sotto il cursore (delegazione eventi, niente re-render).
  const gridRef = useRef(null);
  const hoverColRef = useRef(-1);
  const onGridOver = (e) => {
    const grid = gridRef.current;
    if (!grid) return;
    const td = e.target.closest ? e.target.closest("td") : null;
    const idx = td ? td.cellIndex : -1;
    if (idx === hoverColRef.current) return;
    hoverColRef.current = idx;
    grid.querySelectorAll("td.col-hover").forEach((c) => c.classList.remove("col-hover"));
    if (idx >= 0) grid.querySelectorAll(`tbody tr > td:nth-child(${idx + 1})`).forEach((c) => c.classList.add("col-hover"));
  };
  const onGridLeave = () => {
    hoverColRef.current = -1;
    gridRef.current?.querySelectorAll("td.col-hover").forEach((c) => c.classList.remove("col-hover"));
  };

  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  const load = () => {
    setOrdini(null);
    setSelected(new Set());
    const params = new URLSearchParams({ store_id: storeId, from, to, page: String(page), per_page: "50" });
    if (qDeb.trim()) params.set("q", qDeb.trim());
    Object.entries(filtri).forEach(([k, v]) => { if (v !== "") params.set(k, v); });
    api.get(`/ordini?${params.toString()}`)
      .then((r) => { setOrdini(r.data.risultati); setMeta(r.data); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { setPage(1); }, [storeId, from, to, qDeb, filtriKey]);
  useEffect(load, [storeId, from, to, qDeb, filtriKey, page]);

  const patch = async (o, campo, valore) => {
    try {
      const res = await api.patch(`/ordini/${o.id}`, { [campo]: valore });
      setOrdini((os) => os.map((x) => (x.id === o.id ? { ...res.data, store_nome: x.store_nome } : x)));
      toast.success(`${campo} aggiornato`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ricalcola = async (o) => {
    try {
      const res = await api.post(`/ordini/${o.id}/ricalcola`);
      setOrdini((os) => os.map((x) => (x.id === o.id ? { ...res.data, store_nome: x.store_nome } : x)));
      toast.success("Ordine ricalcolato con i parametri validi alla sua data (vedi Log)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const firstStoreId = storeId !== "all" ? storeId : stores[0]?.id;

  return (
    <div data-testid="ordini-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Griglia ordini</h1>
        {isMaster && (
          <button data-testid="nuovo-ordine-button" onClick={() => setShowNew(true)}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-3 py-2 transition-colors duration-150">
            <Plus className="w-4 h-4" /> Nuovo ordine
          </button>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-3">Clicca su una cella bianca per modificarla. Le celle grigie con il lucchetto sono calcolate e non modificabili. I parametri economici sono congelati sull'ordine alla creazione.</p>

      <div className="flex items-center gap-2 mb-2">
        <input data-testid="ordini-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca N° ordine, cliente o SKU — tutto lo storico…"
          className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        <button data-testid="filtri-open-mobile" onClick={() => setShowFilters(true)}
          className="md:hidden border border-slate-300 rounded-md px-3 py-2 text-sm font-semibold text-slate-600 min-h-[44px]">Filtri</button>
      </div>
      <div className="hidden md:flex flex-wrap items-center gap-2 mb-3">
        <select data-testid="filtro-stato-consegna" value={filtri.stato_consegna} onChange={(e) => setFiltri({ ...filtri, stato_consegna: e.target.value })}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
          <option value="">Consegna: tutte</option>
          {STATI_CONSEGNA.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filtro-stato-conferma" value={filtri.stato_conferma} onChange={(e) => setFiltri({ ...filtri, stato_conferma: e.target.value })}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
          <option value="">Conferma: tutte</option>
          {STATI_CONFERMA.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filtro-metodo" value={filtri.metodo} onChange={(e) => setFiltri({ ...filtri, metodo: e.target.value })}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
          <option value="">Pagamento: tutti</option>
          <optgroup label="Famiglia">{FAMIGLIE.map((s) => <option key={s} value={s}>{s}</option>)}</optgroup>
          <optgroup label="Dettaglio">{DETTAGLI.map((s) => <option key={s} value={s}>{s}</option>)}</optgroup>
        </select>
        <input data-testid="filtro-importo-min" type="number" placeholder="Importo min €" value={filtri.importo_min}
          onChange={(e) => setFiltri({ ...filtri, importo_min: e.target.value })}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 w-28" />
        <input data-testid="filtro-importo-max" type="number" placeholder="Importo max €" value={filtri.importo_max}
          onChange={(e) => setFiltri({ ...filtri, importo_max: e.target.value })}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 w-28" />
      </div>

      {showFilters && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowFilters(false)} />
          <div data-testid="filtri-sheet" className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl p-5 space-y-3 shadow-2xl fade-in">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-800 text-sm">Filtri rapidi</span>
              <button data-testid="filtri-close" onClick={() => setShowFilters(false)} className="text-slate-500 text-sm min-h-[44px] px-2">Chiudi</button>
            </div>
            <select data-testid="filtro-stato-consegna-mobile" value={filtri.stato_consegna} onChange={(e) => setFiltri({ ...filtri, stato_consegna: e.target.value })}
              className="text-sm border border-slate-300 rounded-md px-2.5 py-2.5 bg-white w-full">
              <option value="">Consegna: tutte</option>
              {STATI_CONSEGNA.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="filtro-stato-conferma-mobile" value={filtri.stato_conferma} onChange={(e) => setFiltri({ ...filtri, stato_conferma: e.target.value })}
              className="text-sm border border-slate-300 rounded-md px-2.5 py-2.5 bg-white w-full">
              <option value="">Conferma: tutte</option>
              {STATI_CONFERMA.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="filtro-metodo-mobile" value={filtri.metodo} onChange={(e) => setFiltri({ ...filtri, metodo: e.target.value })}
              className="text-sm border border-slate-300 rounded-md px-2.5 py-2.5 bg-white w-full">
              <option value="">Pagamento: tutti</option>
              <optgroup label="Famiglia">{FAMIGLIE.map((s) => <option key={s} value={s}>{s}</option>)}</optgroup>
              <optgroup label="Dettaglio">{DETTAGLI.map((s) => <option key={s} value={s}>{s}</option>)}</optgroup>
            </select>
            <div className="flex gap-2">
              <input data-testid="filtro-importo-min-mobile" type="number" placeholder="Importo min €" value={filtri.importo_min}
                onChange={(e) => setFiltri({ ...filtri, importo_min: e.target.value })}
                className="text-sm border border-slate-300 rounded-md px-2.5 py-2.5 w-full" />
              <input data-testid="filtro-importo-max-mobile" type="number" placeholder="Importo max €" value={filtri.importo_max}
                onChange={(e) => setFiltri({ ...filtri, importo_max: e.target.value })}
                className="text-sm border border-slate-300 rounded-md px-2.5 py-2.5 w-full" />
            </div>
          </div>
        </div>
      )}

      {meta.ricerca_globale && (
        <div data-testid="ricerca-globale-banner" className="mb-3 flex items-center justify-between bg-amber-50 border border-amber-300 rounded-md px-3 py-2 text-sm text-amber-800">
          <span><b>Ricerca su tutto lo storico</b> — filtro periodo ignorato · {meta.totale} risultati</span>
          <button data-testid="ricerca-reset-button" onClick={() => { setQ(""); setQDeb(""); }}
            className="text-xs font-semibold underline hover:text-amber-900">Torna alla vista filtrata</button>
        </div>
      )}
      {!ordini ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="skeleton h-14" />)}</div>
      ) : (
        <>
        <div className="hidden md:block card overflow-hidden">
          <div ref={gridRef} data-testid="orders-grid-scroll" onMouseOver={onGridOver} onMouseLeave={onGridLeave}
            className="overflow-auto" style={{ maxHeight: "calc(100vh - 18rem)" }}>
          <table data-testid="orders-table" className="text-sm orders-grid">
            <thead>
              <tr className="text-left">
                <th style={stickyStyle(0)} className="sticky top-0 z-30 bg-slate-100 px-2 py-2.5" />
                {COLONNE_ORDINI.filter((c) => !c.masterOnly || isMaster).map((c) => {
                  const si = STICKY_IDX[c.key];
                  return (
                    <th key={c.key} style={si != null ? stickyStyle(si) : undefined}
                      className={`sticky top-0 ${si != null ? "z-30" : "z-20"} bg-slate-100 px-2 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap${si === LAST_STICKY ? " sticky-last" : ""}`}>
                      {c.h}<InfoTip text={c.tip} testid={`tip-col-${c.key}`} />
                    </th>
                  );
                })}
                {COLONNE_CALC.map((c) => (
                  <th key={c.key} className="sticky top-0 z-20 px-2 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-200 text-right whitespace-nowrap">
                    {c.h}<InfoTip text={c.tip} testid={`tip-col-${c.key}`} />
                  </th>
                ))}
                {isMaster && COLONNE_CALC_MASTER.map((c) => (
                  <th key={c.key} className="sticky top-0 z-20 px-2 py-2.5 text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-100 text-right whitespace-nowrap">
                    {c.h}<InfoTip text={c.tip} testid={`tip-col-${c.key}`} />
                  </th>
                ))}
                {isMaster && <th className="sticky top-0 z-20 bg-slate-100 px-2 py-2.5" />}
              </tr>
            </thead>
            <tbody>
              {ordini.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id_ordine_esterno}`} className={selected.has(o.id) ? "row-selected" : ""}>
                  <td style={stickyStyle(0)} className="sticky-l px-2 py-1.5 text-center">
                    <input type="checkbox" data-testid={`row-select-${o.id_ordine_esterno}`} checked={selected.has(o.id)}
                      onChange={() => toggleSel(o.id)} title="Evidenzia la riga (solo per lettura, nessuna azione collegata)"
                      className="accent-indigo-600 w-4 h-4 cursor-pointer align-middle" />
                  </td>
                  <EC testid="cell-data" stickyProps={sp(1)} type="date" value={o.data} display={<span className="font-mono font-semibold text-slate-900">{fmtDataIT(o.data)}</span>} onSave={(v) => patch(o, "data", v)} />
                  <EC testid="cell-id-ordine" stickyProps={sp(2)} value={o.id_ordine_esterno} onSave={(v) => patch(o, "id_ordine_esterno", v)} />
                  {isMaster && <td style={stickyStyle(3)} className="sticky-l truncate px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{o.store_nome}</td>}
                  <EC testid="cell-cliente" stickyProps={sp(LAST_STICKY, true)} value={o.nome_cliente} onSave={(v) => patch(o, "nome_cliente", v)} />
                  <td className="px-2 py-1.5 text-xs whitespace-nowrap">
                    {o.metodo_famiglia
                      ? <span title={o.metodo_dettaglio || undefined}><Badge v={o.metodo_famiglia} map={FAMIGLIA_BADGE} testid={`badge-pagamento-${o.id_ordine_esterno}`} /></span>
                      : o.metodo_dettaglio || <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded px-1">{o.metodo_raw || "—"}</span>}
                  </td>
                  <EC testid="cell-lordo" type="number" value={o.totale_ordine_lordo}
                    display={<span>{fmtEUR(o.calc?.totale_netto_resi ?? o.totale_ordine_lordo)}
                      {o.calc?.totale_reso > 0 && (
                        <span data-testid={`reso-badge-${o.id}`} className="block text-[10px] font-semibold text-red-600"
                          title={`Merce resa: ${fmtEUR(o.calc.totale_reso)}${o.calc.totale_rimborsato > 0 ? ` · denaro restituito: ${fmtEUR(o.calc.totale_rimborsato)}` : " · nessun denaro restituito (es. contrassegno rifiutato)"}`}>
                          reso −{fmtEUR(o.calc.totale_reso)}
                        </span>)}
                    </span>}
                    onSave={(v) => patch(o, "totale_ordine_lordo", v)} />
                  <EC testid="cell-incassato" type="number" value={o.totale_incassato} display={fmtEUR(o.totale_incassato)} onSave={(v) => patch(o, "totale_incassato", v)} />
                  <EC testid="cell-stato-conferma" value={o.stato_conferma} options={STATI_CONFERMA}
                    display={<Badge v={o.stato_conferma} map={CONFERMA_BADGE} />} onSave={(v) => patch(o, "stato_conferma", v)} />
                  <EC testid="cell-stato-ordine" value={o.stato_ordine} options={STATI_ORDINE}
                    display={<Badge v={o.stato_ordine} map={ORDINE_BADGE} />} onSave={(v) => patch(o, "stato_ordine", v)} />
                  <EC testid="cell-stato-consegna" value={o.stato_consegna} options={STATI_CONSEGNA}
                    display={<Badge v={o.stato_consegna} map={CONSEGNA_BADGE} />} onSave={(v) => patch(o, "stato_consegna", v)} />
                  <EC testid="cell-momento-annullo" value={o.momento_annullo} options={MOMENTI_ANNULLO}
                    display={o.momento_annullo ? <Badge v={o.momento_annullo} map={MOMENTO_BADGE} /> : undefined} onSave={(v) => patch(o, "momento_annullo", v)} />
                  <EC testid="cell-reso" value={o.reso} options={RESO_VALORI}
                    display={<Badge v={o.reso} map={RESO_BADGE} />} onSave={(v) => patch(o, "reso", v)} />
                  <EC testid="cell-evento" value={o.evento} options={EVENTI}
                    display={<Badge v={o.evento} map={EVENTO_BADGE} />} onSave={(v) => patch(o, "evento", v)} />
                  <EC testid="cell-data-spedizione" type="date" value={o.data_spedizione} display={o.data_spedizione ? fmtDataIT(o.data_spedizione) : undefined} onSave={(v) => patch(o, "data_spedizione", v)} />
                  <EC testid="cell-data-consegna" type="date" value={o.data_consegna} display={o.data_consegna ? fmtDataIT(o.data_consegna) : undefined} onSave={(v) => patch(o, "data_consegna", v)} />
                  <EC testid="cell-stato-incasso" value={o.stato_incasso_cod} options={STATI_INCASSO_COD}
                    display={<span className="text-xs">{o.stato_incasso_cod ? INCASSO_LABELS[o.stato_incasso_cod] : <span className="text-slate-400">auto: {INCASSO_LABELS[o.stato_incasso_effettivo]}</span>}</span>}
                    onSave={(v) => patch(o, "stato_incasso_cod", v)} />
                  <EC testid="cell-data-incasso" type="date" value={o.data_incasso_cod} display={o.data_incasso_cod ? fmtDataIT(o.data_incasso_cod) : undefined} onSave={(v) => patch(o, "data_incasso_cod", v)} />
                  <EC testid="cell-importo-incassato" type="number" value={o.importo_incassato_cod} display={o.importo_incassato_cod != null ? fmtEUR(o.importo_incassato_cod) : "—"} onSave={(v) => patch(o, "importo_incassato_cod", v)} />
                  <CalcCell testid="calc-imponibile">{fmtEUR(o.calc.vista.imponibile)}</CalcCell>
                  <CalcCell testid="calc-iva">{fmtEUR(o.calc.vista.iva_vendita)}</CalcCell>
                  <CalcCell testid="calc-costo-merce">{fmtEUR(o.calc.vista.costo_merce)}</CalcCell>
                  <CalcCell testid="calc-iva-merce">{fmtEUR(o.calc.vista.iva_merce)}</CalcCell>
                  <CalcCell testid="calc-spedizione">
                    <span title={o.calc.spedizione_stimata ? "Costo spedizione stimato dai parametri, in attesa del costo reale del corriere" : "Costo spedizione reale"}>
                      {o.calc.spedizione_stimata ? "≈ " : ""}{fmtEUR(o.calc.vista.costo_spedizione)}
                    </span>
                  </CalcCell>
                  <CalcCell testid="calc-commissione">{fmtEUR(o.calc.vista.commissione_gateway)}</CalcCell>
                  <CalcCell testid="calc-compensazione">{fmtEUR(o.calc.vista.compensazione)}</CalcCell>
                  <CalcCell testid="calc-margine">{fmtEUR(o.calc.vista.margine)}</CalcCell>
                  {isMaster && (
                    <CalcCell testid="calc-costo-merce-reale">{o.calc.costo_merce_reale != null ? fmtEUR(o.calc.costo_merce_reale) : "—"}</CalcCell>
                  )}
                  {isMaster && (
                    <CalcCell testid="calc-ricarico-merce">
                      {o.calc.ricarico_merce != null ? (
                        <span title={o.calc.righe_ricostruite > 0 ? "Deriva da costi ricostruiti (stimato, non misurato)" : undefined}>
                          {o.calc.righe_ricostruite > 0 ? "≈ " : ""}{fmtEUR(o.calc.ricarico_merce)}
                        </span>
                      ) : "—"}
                    </CalcCell>
                  )}
                  {isMaster && (
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="flex gap-1">
                        <button data-testid="btn-righe" title="Righe ordine" onClick={() => setRigheOrdine(o)} className="p-1.5 text-slate-500 hover:text-indigo-600 transition-colors duration-150"><Receipt className="w-4 h-4" /></button>
                        <button data-testid="btn-ricalcola" title="Ricalcola con parametri alla data" onClick={() => ricalcola(o)} className="p-1.5 text-slate-500 hover:text-indigo-600 transition-colors duration-150"><RefreshCw className="w-4 h-4" /></button>
                        <button data-testid="btn-log" title="Log modifiche" onClick={() => setLogOrdineId(o.id)} className="p-1.5 text-slate-500 hover:text-indigo-600 transition-colors duration-150"><History className="w-4 h-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {ordini.length === 0 && <tr><td colSpan={31} className="px-4 py-8 text-center text-slate-400">{meta.ricerca_globale ? "Nessun risultato in tutto lo storico." : "Nessun ordine nel periodo. Importa un CSV o crea un ordine manuale."}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
        <div className="md:hidden space-y-2 fade-in">
          {ordini.map((o) => (
            <button key={o.id} data-testid={`order-card-${o.id_ordine_esterno}`} onClick={() => setDettaglio(o)}
              className="w-full text-left card p-4 active:bg-slate-50 transition-colors duration-150">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-slate-900">{o.id_ordine_esterno}</div>
                  <div className="text-sm text-slate-600 truncate">{o.nome_cliente || "—"}</div>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">{fmtDataIT(o.data)}{isMaster ? ` · ${o.store_nome}` : ""}</div>
                </div>
                <Badge v={o.stato_consegna} map={CONSEGNA_BADGE} />
              </div>
              <div className="flex items-end justify-between mt-3 gap-2">
                <div className="flex flex-wrap gap-1 min-w-0">
                  {o.metodo_famiglia
                    ? <Badge v={o.metodo_famiglia} map={FAMIGLIA_BADGE} />
                    : <span className="text-xs text-slate-400">{o.metodo_dettaglio || o.metodo_raw || "—"}</span>}
                  {o.stato_conferma && <Badge v={o.stato_conferma} map={CONFERMA_BADGE} />}
                  {o.evento && o.evento !== "Nessuno" && <Badge v={o.evento} map={EVENTO_BADGE} />}
                  {o.reso === "Sì" && <Badge v={o.reso} map={RESO_BADGE} />}
                  {o.momento_annullo === "Dopo" && <Badge v={o.momento_annullo} map={MOMENTO_BADGE} />}
                </div>
                <span className="font-mono font-bold text-lg text-slate-900 tnum shrink-0">{fmtEUR(o.totale_incassato)}</span>
              </div>
            </button>
          ))}
          {ordini.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-8">
              {meta.ricerca_globale ? "Nessun risultato in tutto lo storico." : "Nessun ordine nel periodo."}
            </div>
          )}
        </div>
        </>
      )}
      {ordini && meta.totale > 0 && (
        <div data-testid="ordini-pagination" className="flex items-center gap-3 mt-3 text-sm text-slate-600">
          <button data-testid="page-prev" disabled={meta.page <= 1} onClick={() => setPage((p) => p - 1)}
            className="border border-slate-300 rounded px-2 py-1 disabled:opacity-40 hover:bg-slate-50">← Prec.</button>
          <span className="font-mono text-xs">Pagina {meta.page} di {Math.max(1, Math.ceil(meta.totale / meta.per_page))} — {meta.totale} ordini</span>
          <button data-testid="page-next" disabled={meta.page * meta.per_page >= meta.totale} onClick={() => setPage((p) => p + 1)}
            className="border border-slate-300 rounded px-2 py-1 disabled:opacity-40 hover:bg-slate-50">Succ. →</button>
        </div>
      )}
      {righeOrdine && <RigheDialog ordine={righeOrdine} onClose={() => setRigheOrdine(null)}
        onSaved={(updated) => setOrdini((os) => os.map((x) => (x.id === updated.id ? { ...updated, store_nome: x.store_nome } : x)))} />}
      <DettaglioOrdine ordine={dettaglio} onClose={() => setDettaglio(null)} />
      <LogDialog ordineId={logOrdineId} onClose={() => setLogOrdineId(null)} />
      <NuovoOrdineDialog open={showNew} onClose={() => setShowNew(false)} stores={stores}
        defaultStoreId={firstStoreId} isMaster={isMaster}
        onCreated={() => load()} />
    </div>
  );
}
